import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Eye, Info, Pencil } from "lucide-react";
import api from "../../../services/api";
import { BackButton } from "../../../components/button/back-button";
import { Table } from "../../../components/table";
import { ViewConversationModal } from "../../../components/view-conversation-modal";
import { useToast } from "../../../contexts/toast/toast-context";
import {
  formatUserPhone,
  formatZipCode,
  getApiErrorMessage,
} from "../../../utils";
import { ClientAttachmentsSection } from "./client-attachments-section";
import type {
  Appointment,
  AppointmentStatus,
} from "../../../types/appointment";
import { COMMERCIAL_STATUS_META, type User } from "../../../types/user";
import type { Conversation } from "../../../types/conversation";

type ConversationDto = {
  id: string;
  appName: string;
  userName: string;
  userPhone: string;
  createdAt: Date;
};

type ConversationListDto = {
  data: Array<ConversationDto>;
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

type AppointmentListDto = {
  data: Appointment[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

const APPOINTMENT_STATUS_META: Record<
  AppointmentStatus | "CONFIRMED",
  { label: string; className: string }
> = {
  SCHEDULED: {
    label: "Agendado",
    className: "badge badge-warning",
  },
  CONFIRMED: {
    label: "Confirmado",
    className: "badge badge-success",
  },
  CANCELED: {
    label: "Cancelado",
    className: "badge badge-danger",
  },
};

function formatDate(value?: Date | string) {
  if (!value) return "-";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [year, month, day] = value.slice(0, 10).split("-");
    return `${day}/${month}/${year}`;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatBoolean(value?: boolean) {
  return value ? "Sim" : "Não";
}

export default function VisualizarCliente() {
  const { error } = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [user, setUser] = useState<User | null>(null);
  const [conversations, setConversations] = useState<ConversationDto[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);

  const [selectedConversation, setSelectedConversation] =
    useState<Conversation | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [appointmentsPage, setAppointmentsPage] = useState(1);
  const [appointmentsTotalPages, setAppointmentsTotalPages] = useState(1);
  const [appointmentsLimit] = useState(10);
  const [appointmentsTotal, setAppointmentsTotal] = useState(0);

  const aiContextEntries = useMemo(() => {
    if (!user?.aiContext || Object.keys(user.aiContext).length === 0) {
      return [];
    }

    return Object.entries(user.aiContext);
  }, [user?.aiContext]);

  const fetchUser = useCallback(async () => {
    if (!id) return;

    try {
      const response = await api.get<User>(`/user/${id}`);
      setUser(response.data);
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    }
  }, [id, error]);

  const fetchConversations = useCallback(
    async (pageNumber = 1) => {
      if (!id) return;

      try {
        const response = await api.get<ConversationListDto>(
          `/user/${id}/conversations`,
          {
            params: { page: pageNumber, limit },
          },
        );

        setConversations(response.data.data);
        setPage(response.data.page);
        setTotal(response.data.total);
        setTotalPages(response.data.totalPages);
      } catch (err) {
        const apiMessage = getApiErrorMessage(err);
        error(apiMessage);
      }
    },
    [id, limit, error],
  );

  const fetchAppointments = useCallback(
    async (pageNumber = 1) => {
      if (!id) return;

      try {
        const response = await api.get<AppointmentListDto>("/scheduling", {
          params: { page: pageNumber, limit: appointmentsLimit, userId: id },
        });

        setAppointments(response.data.data);
        setAppointmentsPage(response.data.page);
        setAppointmentsTotal(response.data.total);
        setAppointmentsTotalPages(response.data.totalPages);
      } catch (err) {
        const apiMessage = getApiErrorMessage(err);
        error(apiMessage);
      }
    },
    [id, appointmentsLimit, error],
  );

  useEffect(() => {
    fetchUser();
    fetchConversations(1);
    fetchAppointments(1);
  }, [fetchUser, fetchConversations, fetchAppointments]);

  async function openClosedConversation(conversationId: string) {
    try {
      const response = await api.get(
        `/conversation/get-closed-by-id/${conversationId}`,
      );
      setSelectedConversation(response.data);
      setModalOpen(true);
    } catch (err) {
      const apiMessage = getApiErrorMessage(err);
      error(apiMessage);
    }
  }

  const columns = [
    { key: "createdAt", label: "Data de encerramento" },
    { key: "appName", label: "Aplicação" },
    {
      key: "actions",
      label: "Ações",
      render: (conversation: ConversationDto) => (
        <button
          onClick={() => openClosedConversation(conversation.id)}
          className="btn-link-primary cursor-pointer"
        >
          <Eye className="w-4 h-4" />
        </button>
      ),
    },
  ];

  const appointmentColumns = [
    {
      key: "scheduledAt",
      label: "Data do agendamento",
      render: (appointment: Appointment) => {
        if (!appointment.scheduledAt) return "-";
        const date = new Date(appointment.scheduledAt);
        if (Number.isNaN(date.getTime())) return "-";
        return date.toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
    },
    {
      key: "status",
      label: "Status",
      render: (appointment: Appointment) => {
        const meta =
          appointment.status === "SCHEDULED" && appointment.confirmed
            ? APPOINTMENT_STATUS_META.CONFIRMED
            : APPOINTMENT_STATUS_META[appointment.status];
        return <span className={meta.className}>{meta.label}</span>;
      },
    },
    {
      key: "confirmedAt",
      label: "Data da confirmação",
      render: (appointment: Appointment) => {
        if (!appointment.confirmedAt) return "-";
        const date = new Date(appointment.confirmedAt);
        if (Number.isNaN(date.getTime())) return "-";
        return date.toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
    },
    {
      key: "agentType",
      label: "Agendado por",
      render: (appointment: Appointment) => {
        if (appointment.agentType === "AI") {
          return "Assistente virtual";
        }

        return appointment.systemUser?.name
          ? `Atendente: ${appointment.systemUser.name}`
          : "Atendente";
      },
    },
    {
      key: "duration",
      label: "Duração",
      render: (appointment: Appointment) =>
        appointment.duration ? `${appointment.duration} min` : "-",
    },
    {
      key: "additionalInfo",
      label: "Info adicional",
      render: (appointment: Appointment) => {
        const trimmedAdditionalInfo = appointment.additionalInfo?.trim();

        if (!trimmedAdditionalInfo) return "-";

        return (
          <span
            title={trimmedAdditionalInfo}
            aria-label={`Informações adicionais: ${trimmedAdditionalInfo}`}
            className="inline-flex items-center justify-center text-slate-500 cursor-help"
          >
            <Info className="w-4 h-4" />
          </span>
        );
      },
    },
  ];

  return (
    <div className="app-page">
      <BackButton />
      <div className="flex items-center justify-between mb-6">
        <h1 className="app-page-title">Visualização do Contato</h1>
        {user?.id && (
          <button
            onClick={() => navigate(`/painel/contatos/editar/${user.id}`)}
            className="btn btn-neutral cursor-pointer"
          >
            <Pencil className="w-4 h-4" /> Editar Contato
          </button>
        )}
      </div>

      <div className="app-card mb-8 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="block text-xs uppercase text-slate-500">Nome</span>
            <span className="text-slate-800 font-medium">
              {user?.userName || "-"}
            </span>
          </div>
          <div>
            <span className="block text-xs uppercase text-slate-500">
              Telefone
            </span>
            <span className="text-slate-800 font-medium">
              {user ? formatUserPhone(user) || "-" : "-"}
            </span>
          </div>
          <div>
            <span className="block text-xs uppercase text-slate-500">
              Data de Nascimento
            </span>
            <span className="text-slate-800">
              {formatDate(user?.birthDate)}
            </span>
          </div>
          <div>
            <span className="block text-xs uppercase text-slate-500">
              Email
            </span>
            <span className="text-slate-800">{user?.email || "-"}</span>
          </div>
          <div>
            <span className="block text-xs uppercase text-slate-500">
              Status comercial
            </span>
            {user && (
              <span
                className={
                  COMMERCIAL_STATUS_META[user.commercialStatus ?? "UNQUALIFIED"]
                    .className
                }
              >
                {
                  COMMERCIAL_STATUS_META[user.commercialStatus ?? "UNQUALIFIED"]
                    .label
                }
              </span>
            )}
          </div>
          <div>
            <span className="block text-xs uppercase text-slate-500">Cep</span>
            <span className="text-slate-800">
              {formatZipCode(user?.zipCode || "") || "-"}
            </span>
          </div>
          <div>
            <span className="block text-xs uppercase text-slate-500">
              Cidade
            </span>
            <span className="text-slate-800">{user?.city || "-"}</span>
          </div>
          <div>
            <span className="block text-xs uppercase text-slate-500">País</span>
            <span className="text-slate-800">{user?.country || "-"}</span>
          </div>
          <div>
            <span className="block text-xs uppercase text-slate-500">
              Logradouro
            </span>
            <span className="text-slate-800">{user?.addressLine || "-"}</span>
          </div>
          <div>
            <span className="block text-xs uppercase text-slate-500">
              Número
            </span>
            <span className="text-slate-800">{user?.addressNumber || "-"}</span>
          </div>
          <div>
            <span className="block text-xs uppercase text-slate-500">
              Complemento
            </span>
            <span className="text-slate-800">{user?.complement || "-"}</span>
          </div>
          <div>
            <span className="block text-xs uppercase text-slate-500">
              Bairro
            </span>
            <span className="text-slate-800">{user?.neighborhood || "-"}</span>
          </div>
          <div className="md:col-span-2">
            <span className="block text-xs uppercase text-slate-500">
              Consentimento de marketing via WhatsApp
            </span>
            <span className="badge mt-1">
              {!user?.contactConsent
                ? "Não solicitado"
                : user.contactConsent.status === "GRANTED"
                  ? "Aceito"
                  : user.contactConsent.status === "DENIED"
                    ? "Recusado"
                    : "Aguardando resposta"}
            </span>
            {user?.contactConsent && (
              <div className="mt-2 space-y-1 text-sm text-slate-600">
                <p>
                  Solicitado em:{" "}
                  {user.contactConsent.requestedAt
                    ? new Date(user.contactConsent.requestedAt).toLocaleString(
                        "pt-BR",
                      )
                    : "-"}
                </p>
                <p>
                  Respondido em:{" "}
                  {user.contactConsent.respondedAt
                    ? new Date(user.contactConsent.respondedAt).toLocaleString(
                        "pt-BR",
                      )
                    : "-"}
                </p>
              </div>
            )}
          </div>
          <div>
            <span className="block text-xs uppercase text-slate-500">
              Aceita Email
            </span>
            <span className="text-slate-800">
              {formatBoolean(user?.acceptsEmailCommunications)}
            </span>
          </div>
          <div className="md:col-span-2">
            <span className="block text-xs uppercase text-slate-500">
              Informações adicionais
            </span>
            <span className="text-slate-800">
              {user?.additionalInfo || "-"}
            </span>
          </div>
        </div>

        <div className="mt-4">
          <span className="block text-xs uppercase text-slate-500">
            Memória IA
          </span>
          <div className="mt-2 overflow-hidden rounded-lg border border-slate-200 bg-white">
            <div className="hidden grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold uppercase text-slate-500 md:grid">
              <span>Chave</span>
              <span>Valor</span>
            </div>

            {aiContextEntries.length === 0 ? (
              <div className="px-3 py-4 text-sm text-slate-500">
                Sem memória salva.
              </div>
            ) : (
              aiContextEntries.map(([key, value]) => (
                <div
                  key={key}
                  className="grid grid-cols-1 gap-1 border-b border-slate-100 px-3 py-3 last:border-b-0 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] md:gap-2"
                >
                  <div>
                    <span className="block text-[11px] font-semibold uppercase text-slate-400 md:hidden">
                      Chave
                    </span>
                    <span className="break-words text-sm font-medium text-slate-800">
                      {key}
                    </span>
                  </div>
                  <div>
                    <span className="block text-[11px] font-semibold uppercase text-slate-400 md:hidden">
                      Valor
                    </span>
                    <span className="break-words text-sm text-slate-700">
                      {String(value)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {user?.id && (
        <div className="mb-8">
          <ClientAttachmentsSection
            userId={user.id}
            attachments={user.attachments}
            onUserChange={setUser}
          />
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h2 className="app-section-title">Atendimentos encerrados</h2>
      </div>

      <Table<ConversationDto>
        columns={columns}
        data={conversations}
        page={page}
        total={total}
        totalPages={totalPages}
        onPageChange={(newPage) => fetchConversations(newPage)}
      />

      <ViewConversationModal
        isOpen={modalOpen}
        conversation={selectedConversation}
        onClose={() => setModalOpen(false)}
      />

      <div className="flex items-center justify-between mb-4 mt-10">
        <h2 className="app-section-title">Agendamentos</h2>
      </div>

      <Table<Appointment>
        columns={appointmentColumns}
        data={appointments}
        page={appointmentsPage}
        total={appointmentsTotal}
        totalPages={appointmentsTotalPages}
        onPageChange={(newPage) => fetchAppointments(newPage)}
      />
    </div>
  );
}
